import { useState, useEffect, useMemo } from 'react'
import { InsertCategories, SlideoutMenu, MenuTabs } from 'components'
import Flex from 'components/priority/Flex/Flex'
import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil'
import { insertMenuOpenAtom, applicationTypeAtom, caseAtom } from 'atoms'
import zLayers from 'utils/styling/zLayers'
import { useTranslation } from 'react-i18next'
import { M3 } from '@messenger/consts'
import { isBulkProcessingAtom } from '@/atoms/_editorAtoms'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'
import { useCaseImageVariants } from '@/hooks/caseImages'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { SERVICE_INFORMATION_TEMPLATES_LAUNCHED } from '@/components/editor/ServiceTemplatesCustomizer/serviceInformationTemplatesLaunch'

/** The Case tab menu where a designer manages and enhances the case's photos. */
const CASE_IMAGES_MENU = 'ImagesAndFamilyMemorials'

export const InsertMenu = ({
  basePages,
  baseLayout,
  setIsLoading,
  update,
  itemsLocked,
}) => {
  useEditorProbeLifetime('InsertMenu')
  const { t, i18n } = useTranslation()
  const [showSlideout, setShowSlideout] = useRecoilState(insertMenuOpenAtom)
  const [selectedMenu, setSelectedMenu] = useState('')
  const [activeTab, setActiveTab] = useState('Assets')
  const applicationType = useRecoilValue(applicationTypeAtom)
  const isBulkProcessing = useRecoilValue(isBulkProcessingAtom)
  const { loadCaseImages } = useCaseImageVariants()
  const handleError = useErrorHandler('InsertMenu')
  // Read when the menu opens rather than subscribed to, so editing the case
  // does not re-render the insert menu.
  const readCaseId = useRecoilCallback(
    ({ snapshot }) =>
      () =>
        snapshot.getLoadable(caseAtom).valueMaybe()?.id,
    [],
  )

  const setMenu = (menu) => {
    const opening = selectedMenu !== menu || !showSlideout
    setShowSlideout(opening)
    setSelectedMenu(menu)
    // The editor loads the case's photos when it opens, but not their AI
    // variants or the variant pricing. The photos menu is where a designer
    // enhances a photo, so opening it loads all three, the way the case
    // details modal does when it opens.
    const caseId = opening && menu === CASE_IMAGES_MENU ? readCaseId() : null
    if (caseId) {
      loadCaseImages(caseId).catch((error) =>
        handleError(error?.response?.data?.message || error.message, error),
      )
    }
  }

  const menuConfigAssets = useMemo(
    () => {
      const baseMenuItems = [
        {
          name: 'SavedLayouts',
          allowedFor: ['fh-user', 'messenger-user'],
          displayLabel: t('editor.tabs.assets.savedLayoutsLabel'),
        },
        {
          name: 'PhotosOfDeceased',
          allowedFor: ['fh-user', 'messenger-user'],
          displayLabel: t('editor.tabs.assets.photosOfDeceasedLabel'),
          locked: itemsLocked,
        },
        {
          name: 'StylizedVerses',
          allowedFor: ['fh-user', 'messenger-user'],
          displayLabel: t('editor.tabs.assets.stylizedVersesLabel'),
          locked: itemsLocked,
        },
        {
          name: 'EditableVersesAndPhrases',
          allowedFor: ['fh-user', 'messenger-user'],
          displayLabel: t('editor.tabs.assets.editableVersesLabel'),
          locked: itemsLocked,
        },
        {
          name: 'DesignAssets',
          allowedFor: ['messenger-user'],
          displayLabel: t('editor.tabs.assets.designAssetsLabel'),
          locked: false,
        },
        ...(SERVICE_INFORMATION_TEMPLATES_LAUNCHED
          ? [
              {
                name: 'ServiceInformationTemplates',
                allowedFor: ['fh-user', 'messenger-user'],
                displayLabel: t(
                  'editor.tabs.assets.serviceInformationTemplatesLabel',
                ),
                locked: itemsLocked,
              },
            ]
          : []),
      ]

      // Filter out EditableVersesAndPhrases for M3 application type
      return applicationType === M3
        ? baseMenuItems.filter(
            (item) => item.name !== 'EditableVersesAndPhrases',
          )
        : baseMenuItems
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemsLocked, t, i18n.language, applicationType],
  )

  const menuConfigCase = useMemo(
    () => [
      {
        name: 'DeceasedInfo',
        allowedFor: ['fh-user', 'messenger-user'],
        displayLabel: t('editor.tabs.case.deceasedInfoLabel'),
      },
      {
        name: 'Obituary',
        allowedFor: ['fh-user', 'messenger-user'],
        displayLabel: t('editor.tabs.case.obituaryLabel'),
      },
      {
        name: 'ServiceInfo',
        allowedFor: ['fh-user', 'messenger-user'],
        displayLabel: t('editor.tabs.case.serviceInfoLabel'),
      },
      {
        name: 'ImagesAndFamilyMemorials',
        allowedFor: ['messenger-user'],
        displayLabel: t('editor.tabs.case.imagesAndFamilyMemorialsLabel'),
      },
      {
        name: 'MemorialContributions',
        allowedFor: ['messenger-user'],
        displayLabel: t('editor.tabs.case.memorialContributionsLabel'),
      },
      {
        name: 'CasePreferences',
        allowedFor: ['fh-user', 'messenger-user'],
        displayLabel: t('editor.tabs.case.casePreferencesLabel'),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  )

  const [menuItems, setMenuItems] = useState(menuConfigAssets)

  useEffect(() => {
    setMenuItems(activeTab === 'Assets' ? menuConfigAssets : menuConfigCase)
    setShowSlideout(false)
  }, [activeTab, menuConfigAssets, menuConfigCase, setShowSlideout])

  return (
    <Flex
      height="100%"
      width="250px"
      background="white"
      position="relative"
      zindex={zLayers.base}
      borderRight="1px solid"
      borderColor="gray4"
      column
      data-editor-ui="insert-menu"
      pointerEvents={isBulkProcessing ? 'none' : 'auto'}
    >
      <MenuTabs
        activeTab={[activeTab, setActiveTab]}
        tabs={['Assets', 'Case']}
      />

      <InsertCategories
        setMenu={setMenu}
        showSlideout={showSlideout}
        selectedMenu={selectedMenu}
        setSelectedMenu={setSelectedMenu}
        setShowSlideout={setShowSlideout}
        menuConfig={menuItems}
      />

      <SlideoutMenu
        isSearchMenu={selectedMenu === 'SearchBar'}
        setIsLoading={setIsLoading}
        title={selectedMenu}
        show={[showSlideout, setShowSlideout]}
        menu={selectedMenu}
        basePages={basePages}
        baseLayout={baseLayout}
        update={update}
      />
    </Flex>
  )
}

export default InsertMenu
